import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  OnDestroy
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { forkJoin, of, Subject, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';

import { OpenapiPropertiesEditorComponent } from '@shared/components/openapi-properties-editor/openapi-properties-editor';
import { PropertyDefinition } from '@shared/models/property-schema';
import {
  openApiSchemaToProperties,
  propertiesToOpenApiSchema
} from '@shared/utils/openapi-schema-utils';
import { AdminService } from '@generated/services/admin.service';
import { ModelsService } from '@generated/services/models.service';
import { descriptorEntriesToDict } from '@shared/utils/openapi-schema-utils';
import { BenchmarkSummaryDto } from '@generated/models/benchmark-summary-dto';
import { VersionBenchmarkLinkInput } from '@generated/models/version-benchmark-link-input';
import { OpenApiPropertiesSchema } from '@generated/models/open-api-properties-schema';
import { NotificationService } from '@core/services/notification.service';

/**
 * Admin-only version-level edit payload. После SKN-290 из PUT'а исчезли
 * benchmark_messages / benchmark_temperature / auto_benchmark_enabled —
 * эти настройки перешли на уровень Benchmark сущности (общая для всех
 * версий, привязана к типу LLM/STT). Здесь только то, что реально edit'ит
 * админ per-version: schema, is_recommended, pricing.
 */
export interface ModelPropertiesEditRequest {
  versionId: string;
  versionName: string;
  modelName: string;
  /** "llm" | "stt" | "tts" | "vad" — drives which pricing inputs render. */
  modelTypeCode: string;
  /** modelTypeId — нужен для фильтрации совместимых бенчмарков через
   *  GET /admin/benchmarks?modelTypeId=. Если не передан, секция бенчей
   *  скрывается. */
  modelTypeId?: string | null;
  additionalProperties: OpenApiPropertiesSchema | null;
  isRecommended: boolean;
  inputPricePerMTokens: number | null;
  inputCachedPricePerMTokens: number | null;
  inputCacheWritePricePerMTokens: number | null;
  outputPricePerMTokens: number | null;
  pricePerMCharacters: number | null;
  pricePerMinute: number | null;
}

@Component({
  selector: 'app-model-properties-drawer',
  standalone: true,
  imports: [
    FormsModule,
    DrawerModule,
    ButtonModule,
    ToggleSwitchModule,
    InputNumberModule,
    TableModule,
    TooltipModule,
    MultiSelectModule,
    OpenapiPropertiesEditorComponent
],
  templateUrl: './model-properties-drawer.component.html',
  styleUrl: './model-properties-drawer.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModelPropertiesDrawerComponent implements OnDestroy {
  private adminService = inject(AdminService);
  private modelsService = inject(ModelsService);
  private notificationService = inject(NotificationService);
  private destroy$ = new Subject<void>();

  readonly visible = input<boolean>(false);
  readonly request = input<ModelPropertiesEditRequest | null>(null);
  readonly visibleChange = output<boolean>();
  readonly saved = output<{
    versionId: string;
    additionalProperties: OpenApiPropertiesSchema | null;
    isRecommended: boolean;
    inputPricePerMTokens: number | null;
    inputCachedPricePerMTokens: number | null;
    inputCacheWritePricePerMTokens: number | null;
    outputPricePerMTokens: number | null;
    pricePerMCharacters: number | null;
    pricePerMinute: number | null;
  }>();

  readonly properties = signal<PropertyDefinition[]>([]);
  readonly isRecommended = signal<boolean>(false);
  // Benchmarks: список совместимых (по modelTypeId) + текущий выбранный
  // набор benchmarkId'ов. Массив, а не Set — чтобы p-multiSelect по
  // ngModel сравнивал с options'ами. Full-replace семантика при save'e.
  readonly availableBenchmarks = signal<BenchmarkSummaryDto[]>([]);
  readonly selectedBenchmarkIds = signal<string[]>([]);
  /** Per-link overrides: benchmarkId → { temperature/top_k/top_p/max_output_tokens }.
   *  Держим отдельно от selectedBenchmarkIds чтобы не смешивать «выбран
   *  ли» с «какие параметры».  Значение null = fallback на defaults бенча
   *  или model schema (бэк решает). */
  readonly linkOverrides = signal<Map<string, VersionBenchmarkLinkInput>>(new Map());
  readonly isLoadingBenchmarks = signal<boolean>(false);
  readonly inputPricePerMTokens = signal<number | null>(null);
  readonly inputCachedPricePerMTokens = signal<number | null>(null);
  readonly inputCacheWritePricePerMTokens = signal<number | null>(null);
  readonly outputPricePerMTokens = signal<number | null>(null);
  readonly pricePerMCharacters = signal<number | null>(null);
  readonly pricePerMinute = signal<number | null>(null);
  readonly isSaving = signal<boolean>(false);
  /** SKN-313: additional_properties больше не приходят в hierarchy,
   *  drawer сам подгружает свежую schema через /api/Models/versions/{id}.
   *  schemaState гейтит save — не сохраняем пока не увидели актуальную
   *  схему (иначе перезатрём пустышкой). */
  readonly schemaState = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  readonly schemaError = signal<string | null>(null);
  readonly modelName = computed(() => this.request()?.modelName ?? '');
  readonly versionName = computed(() => this.request()?.versionName ?? '');
  readonly modelTypeCode = computed(() => (this.request()?.modelTypeCode ?? '').toLowerCase());
  readonly isLlm = computed(() => this.modelTypeCode() === 'llm');
  readonly isTts = computed(() => this.modelTypeCode() === 'tts');
  readonly isStt = computed(() => this.modelTypeCode() === 'stt');

  readonly title = $localize`:@@modelProperties.title:Additional properties`;

  constructor() {
    // Reload every time a new request lands so reopening for a different
    // version doesn't carry the previous version's data over.
    effect(() => {
      const req = this.request();
      this.isSaving.set(false);
      // Открыли drawer — сразу тянем свежую schema через /api/Models/versions/{id}.
      // Пока не пришёл ответ, save заблокирован (см. canSave), чтобы
      // случайно не отправить пустой properties[] и не перезатереть.
      this.properties.set([]);
      this.schemaState.set('idle');
      this.schemaError.set(null);
      this.isRecommended.set(req?.isRecommended ?? false);
      this.inputPricePerMTokens.set(req?.inputPricePerMTokens ?? null);
      this.inputCachedPricePerMTokens.set(req?.inputCachedPricePerMTokens ?? null);
      this.inputCacheWritePricePerMTokens.set(req?.inputCacheWritePricePerMTokens ?? null);
      this.outputPricePerMTokens.set(req?.outputPricePerMTokens ?? null);
      this.pricePerMCharacters.set(req?.pricePerMCharacters ?? null);
      this.pricePerMinute.set(req?.pricePerMinute ?? null);

      // Загружаем совместимые бенчи и определяем какие уже привязаны к
      // этой версии. Только когда drawer открыт и известен modelTypeId
      // (без него GET /admin/benchmarks?modelTypeId= неоднозначен).
      this.availableBenchmarks.set([]);
      this.selectedBenchmarkIds.set([]);
      this.linkOverrides.set(new Map());
      if (this.visible() && req?.versionId) {
        this.loadVersionSchema(req.versionId);
        if (req.modelTypeId) this.loadBenchmarks(req.modelTypeId, req.versionId);
      }
    });
  }

  loadVersionSchema(versionId: string): void {
    this.schemaState.set('loading');
    this.schemaError.set(null);
    this.modelsService.apiModelsVersionsModelVersionIdGet({ modelVersionId: versionId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (full) => {
          // Полная schema для этой версии живёт в самой version;
          // ModelType и Model additional_properties — inherit / shared,
          // редактируем на другом экране. Здесь показываем именно то что
          // прикручено к версии.
          this.properties.set(this.parse(full.additional_properties as OpenApiPropertiesSchema | null));
          this.schemaState.set('ready');
        },
        error: (err) => {
          this.schemaError.set(err?.message ?? 'Failed to load version schema');
          this.schemaState.set('error');
        }
      });
  }

  retryLoadSchema(): void {
    const req = this.request();
    if (req?.versionId) this.loadVersionSchema(req.versionId);
  }

  private loadBenchmarks(modelTypeId: string, versionId: string): void {
    this.isLoadingBenchmarks.set(true);
    this.adminService.apiAdminBenchmarksGet({ modelTypeId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          const bench = list ?? [];
          this.availableBenchmarks.set(bench);
          // Prefill: делаем forkJoin GET detail'ов по всем бенчам,
          // смотрим в BenchmarkDetailDto.model_versions есть ли наша
          // versionId. Собираем benchmarkId'ы куда наша версия уже
          // прикручена — они станут дефолт-выбранными в p-multiSelect.
          if (bench.length === 0) {
            this.selectedBenchmarkIds.set([]);
            this.isLoadingBenchmarks.set(false);
            return;
          }
          forkJoin(
            bench.map(b => b.id
              ? this.adminService.apiAdminBenchmarksBenchmarkIdGet({ benchmarkId: b.id })
                  .pipe(catchError(() => of(null)))
              : of(null)
            )
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe((details) => {
            const preselected: string[] = [];
            const overrides = new Map<string, VersionBenchmarkLinkInput>();
            details.forEach((d) => {
              if (!d?.id) return;
              const link = (d.model_versions ?? [])
                .find(l => l.model_version_id === versionId);
              if (link) {
                preselected.push(d.id);
                overrides.set(d.id, {
                  benchmark_id: d.id,
                  additional_properties: link.additional_properties ?? null,
                  temperature: link.temperature ?? null,
                  max_output_tokens: link.max_output_tokens ?? null,
                  top_k: link.top_k ?? null,
                  top_p: link.top_p ?? null,
                });
              }
            });
            this.selectedBenchmarkIds.set(preselected);
            this.linkOverrides.set(overrides);
            this.isLoadingBenchmarks.set(false);
          });
        },
        error: (error) => {
          console.error('Failed to load benchmarks list:', error);
          this.isLoadingBenchmarks.set(false);
        }
      });
  }

  /** Обновление одного sampling-параметра per-link. Пересобираем Map
   *  чтобы signal триггерил CD. Значение null = удаляем override
   *  (при save отправим просто null). */
  updateLinkParam(
    benchmarkId: string,
    key: 'temperature' | 'max_output_tokens' | 'top_k' | 'top_p',
    value: number | null,
  ): void {
    const next = new Map(this.linkOverrides());
    const prev = next.get(benchmarkId) ?? {
      benchmark_id: benchmarkId,
      additional_properties: null,
      temperature: null,
      max_output_tokens: null,
      top_k: null,
      top_p: null,
    };
    next.set(benchmarkId, { ...prev, [key]: value });
    this.linkOverrides.set(next);
  }

  /** Getter для чтения одного параметра из Map — удобно из шаблона. */
  linkParamValue(
    benchmarkId: string,
    key: 'temperature' | 'max_output_tokens' | 'top_k' | 'top_p',
  ): number | null {
    return this.linkOverrides().get(benchmarkId)?.[key] ?? null;
  }

  /** Только выбранные бенчи в стабильном порядке — для таблицы per-link. */
  selectedBenchmarks(): BenchmarkSummaryDto[] {
    const set = new Set(this.selectedBenchmarkIds());
    return this.availableBenchmarks().filter(b => b.id && set.has(b.id));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private parse(schema: OpenApiPropertiesSchema | null | undefined): PropertyDefinition[] {
    if (!schema) return [];
    try {
      return openApiSchemaToProperties(schema);
    } catch (e) {
      console.error('Failed to parse version additional_properties:', e);
      return [];
    }
  }

  onPropertiesChange(props: PropertyDefinition[]): void {
    this.properties.set(props);
  }

  onRecommendedChange(value: boolean): void {
    this.isRecommended.set(value);
  }

  close(): void {
    if (this.isSaving()) return;
    this.visibleChange.emit(false);
  }

  /** Save заблокирован пока свежая schema не подгрузилась через
   *  /api/Models/versions/{id} — иначе serialize([]) отправит пустышку и
   *  перезатрёт сохранённые properties. */
  readonly canSave = computed(() => this.schemaState() === 'ready' && !this.isSaving());

  save(): void {
    const req = this.request();
    if (!req || this.isSaving()) return;
    if (this.schemaState() !== 'ready') {
      // Не даём кидать save до того, как получили актуальную schema.
      return;
    }

    this.isSaving.set(true);
    const serialized = propertiesToOpenApiSchema(this.properties()) as OpenApiPropertiesSchema;
    const recommended = this.isRecommended();
    const pricing = {
      inputPricePerMTokens: this.inputPricePerMTokens(),
      inputCachedPricePerMTokens: this.inputCachedPricePerMTokens(),
      inputCacheWritePricePerMTokens: this.inputCacheWritePricePerMTokens(),
      outputPricePerMTokens: this.outputPricePerMTokens(),
      pricePerMCharacters: this.pricePerMCharacters(),
      pricePerMinute: this.pricePerMinute()
    };

    // benchmarks: full-replace семантика по контракту бэка (null — не
    // трогаем, [] — очищаем, непустой — replace). Мы отправляем всегда
    // текущий набор, включая пустой; так админ сможет explicitly
    // отвязать все бенчи, сняв чекбоксы.
    // Full-replace: собираем линковку в актуальный набор + берём
    // sampling-параметры из linkOverrides если админ их выставил.
    const overrides = this.linkOverrides();
    const benchmarks: VersionBenchmarkLinkInput[] = req.modelTypeId
      ? this.selectedBenchmarkIds().map(id => {
          const o = overrides.get(id);
          return {
            benchmark_id: id,
            additional_properties: o?.additional_properties ?? null,
            temperature: o?.temperature ?? null,
            max_output_tokens: o?.max_output_tokens ?? null,
            top_k: o?.top_k ?? null,
            top_p: o?.top_p ?? null,
          };
        })
      : [];

    this.adminService.apiAdminModelVersionsVersionIdPut({
      versionId: req.versionId,
      body: {
        additional_properties: serialized,
        is_recommended: recommended,
        input_price_per_m_tokens: pricing.inputPricePerMTokens ?? 0,
        input_cached_price_per_m_tokens: pricing.inputCachedPricePerMTokens ?? 0,
        input_cache_write_price_per_m_tokens: pricing.inputCacheWritePricePerMTokens ?? 0,
        output_price_per_m_tokens: pricing.outputPricePerMTokens ?? 0,
        price_per_m_characters: pricing.pricePerMCharacters ?? 0,
        price_per_minute: pricing.pricePerMinute ?? 0,
        benchmarks: req.modelTypeId ? benchmarks : null,
      }
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: () => {
        this.notificationService.showSuccess(
          $localize`:@@modelProperties.notify.saved:Properties saved`
        );
        this.isSaving.set(false);
        this.saved.emit({
          versionId: req.versionId,
          additionalProperties: serialized,
          isRecommended: recommended,
          ...pricing
        });
        this.visibleChange.emit(false);
      },
      error: (error) => {
        console.error('Failed to save version properties:', error);
        this.notificationService.showError(
          $localize`:@@modelProperties.notify.saveFailed:Failed to save properties`
        );
        this.isSaving.set(false);
      }
    });
  }
}
